import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { referenceJobFrameFetcher } from 'utils/frame-thumbnails';
import type { Detection } from './reference-model';

const WIDTH = 96;
const HEIGHT = 72;
const cache = new Map<string, string>();

// Fetched on our own so the editor's frame decoder is never disturbed. The last few frames stay,
// so the detections of one photo share a single download; a failed fetch is dropped and retried.
const FRAMES_KEPT = 4;
const frames = new Map<string, Promise<Blob>>(); // least recently used first

function fetchFrame(jobId: number, frame: number): Promise<Blob> {
    const key = `${jobId}:${frame}`;
    let pending = frames.get(key);
    if (pending) {
        frames.delete(key);
    } else {
        const request = referenceJobFrameFetcher(jobId)(frame);
        request.catch(() => { if (frames.get(key) === request) frames.delete(key); });
        pending = request;
    }
    frames.set(key, pending);
    while (frames.size > FRAMES_KEPT) {
        const oldest = frames.keys().next().value;
        if (oldest === undefined) break;
        frames.delete(oldest);
    }
    return pending;
}

export async function cropDetection(jobId: number, frame: number, points: number[]): Promise<string | null> {
    const key = `${jobId}:${frame}:${points.join(',')}`;
    const cached = cache.get(key);
    if (cached) return cached;

    let image: ImageBitmap | null = null;
    try {
        image = await createImageBitmap(await fetchFrame(jobId, frame));

        const xs = points.filter((_, index) => index % 2 === 0);
        const ys = points.filter((_, index) => index % 2 === 1);
        const x = Math.max(0, Math.min(...xs));
        const y = Math.max(0, Math.min(...ys));
        const width = Math.min(image.width, Math.max(...xs)) - x;
        const height = Math.min(image.height, Math.max(...ys)) - y;
        if (!(width > 0 && height > 0)) throw new Error('empty box');

        const canvas = document.createElement('canvas');
        canvas.width = WIDTH;
        canvas.height = HEIGHT;
        const context = canvas.getContext('2d');
        if (!context) throw new Error('no 2d context');
        const scale = Math.min(WIDTH / width, HEIGHT / height);
        const drawWidth = width * scale;
        const drawHeight = height * scale;
        context.drawImage(
            image, x, y, width, height, (WIDTH - drawWidth) / 2, (HEIGHT - drawHeight) / 2, drawWidth, drawHeight,
        );
        const url = canvas.toDataURL('image/jpeg', 0.8);
        cache.set(key, url);
        return url;
    } catch {
        // not cached, so a later render retries
        return null;
    } finally {
        image?.close();
    }
}

export function useDetectionCrops(detections: Detection[]): Record<number, string> {
    const job = useSelector((state: any) => state.annotation.job.instance);
    const [crops, setCrops] = useState<Record<number, string>>({});
    const signature = detections.map((item) => `${item.clientID}:${item.frame}:${item.points.join(',')}`).join('|');

    useEffect(() => {
        let cancelled = false;
        if (!job) return undefined;
        (async () => {
            // sequential, so decoding many frames does not stall the editor
            for (const item of detections) {
                if (cancelled) return;
                const url = await cropDetection(job.id, item.frame, item.points);
                if (url && !cancelled) setCrops((prev) => ({ ...prev, [item.clientID]: url }));
            }
        })();
        return () => { cancelled = true; };
    }, [job, signature]);

    return crops;
}
